import { useEffect, useState } from "react";
import { Instagram, Facebook, RefreshCw, Unplug, X, Link2, Eye, EyeOff, Trash2 } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

function ProductPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/products", { params: { q, limit: 6 } });
        setResults(data.products);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products to tag…" data-testid="social-product-search" className="tq-input mb-2" />
      <div className="max-h-40 overflow-y-auto border border-[#E5DEC9] divide-y divide-[#F4EFE6]">
        {results.map((p) => (
          <button key={p.id} type="button" onClick={() => onPick(p)} data-testid={`social-pick-${p.slug}`} className="w-full text-left px-3 py-2 text-sm hover:bg-[#F7F2E7]">
            {p.name} <span className="text-xs text-[#9a938a]">({p.sku})</span>
          </button>
        ))}
        {!results.length && <p className="p-3 text-xs text-[#9a938a]">No products found.</p>}
      </div>
    </div>
  );
}

export default function AdminSocial() {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ access_token: "", instagram_business_account_id: "", facebook_page_id: "" });
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/social/status").then(({ data }) => setStatus(data)).catch((e) => toast.error(e.friendlyMessage));
    api.get("/admin/social/items").then(({ data }) => setItems(data)).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, []);

  const connect = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/social/connect", form);
      toast.success(`Connected${data.instagram_username ? ` — @${data.instagram_username}` : ""}${data.page_name ? ` / ${data.page_name}` : ""}`);
      setForm({ access_token: "", instagram_business_account_id: "", facebook_page_id: "" });
      load();
    } catch (err) { toast.error(err.friendlyMessage); } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Instagram & Facebook? Synced posts will remain.")) return;
    await api.post("/admin/social/disconnect");
    toast.success("Disconnected");
    load();
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/social/sync");
      toast.success(`Synced — Instagram: ${data.instagram}, Facebook: ${data.facebook}`);
      load();
    } catch (err) { toast.error(err.friendlyMessage); } finally { setSyncing(false); }
  };

  const saveItem = async () => {
    try {
      await api.put(`/admin/social/items/${editing.source}/${editing.external_id}`, {
        visible: editing.visible, position: editing.position, product_ids: editing.product_ids,
      });
      toast.success("Post updated");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.friendlyMessage); }
  };

  return (
    <div className="max-w-5xl" data-testid="admin-social">
      <div className="mb-6">
        <h1 className="font-serif text-3xl">Instagram & Facebook Sync</h1>
        <p className="text-sm text-[#6E6D6A] mt-1">Auto-import your social posts and make them shoppable on the storefront.</p>
      </div>

      {!status ? <div className="h-40 bg-white animate-pulse" /> : !status.connected ? (
        <div className="grid lg:grid-cols-2 gap-5">
          <form onSubmit={connect} className="bg-white border border-[#D4AF37]/50 p-6" data-testid="social-connect-form">
            <p className="font-semibold mb-4">Connect your accounts</p>
            <label className="tq-overline !text-[10px] block mb-1">Long-lived Access Token *</label>
            <input required value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} data-testid="social-token" className="tq-input mb-3" placeholder="EAA…" />
            <label className="tq-overline !text-[10px] block mb-1">Instagram Business Account ID</label>
            <input value={form.instagram_business_account_id} onChange={(e) => setForm({ ...form, instagram_business_account_id: e.target.value })} data-testid="social-ig-id" className="tq-input mb-3" placeholder="1784…" />
            <label className="tq-overline !text-[10px] block mb-1">Facebook Page ID</label>
            <input value={form.facebook_page_id} onChange={(e) => setForm({ ...form, facebook_page_id: e.target.value })} data-testid="social-fb-id" className="tq-input mb-4" placeholder="10…" />
            <button disabled={busy} data-testid="social-connect-btn" className="tq-btn-dark w-full">{busy ? "Validating with Meta…" : "Connect Accounts"}</button>
          </form>
          <div className="bg-white border border-[#E5DEC9] p-6 text-sm text-[#6E6D6A]" data-testid="social-setup-guide">
            <p className="font-semibold text-[#121212] mb-3">How to get your credentials</p>
            <ol className="list-decimal list-inside space-y-2 text-xs leading-relaxed">
              <li>Go to <span className="font-medium text-[#121212]">developers.facebook.com</span> → create an app (Business type).</li>
              <li>Add the <span className="font-medium text-[#121212]">Instagram Graph API</span> product to the app.</li>
              <li>Generate a User Access Token with permissions: <code className="bg-[#F4EFE6] px-1">instagram_basic</code>, <code className="bg-[#F4EFE6] px-1">pages_show_list</code>, <code className="bg-[#F4EFE6] px-1">pages_read_engagement</code>.</li>
              <li>Exchange it for a <span className="font-medium text-[#121212]">long-lived token</span> (valid 60 days) via the Graph API Explorer.</li>
              <li>Your Instagram account must be a <span className="font-medium text-[#121212]">Business/Creator</span> account linked to your Facebook Page. Find the IG Business Account ID in Graph API Explorer: <code className="bg-[#F4EFE6] px-1">me/accounts?fields=instagram_business_account</code>.</li>
              <li>Tokens are stored encrypted on the server and never exposed to the storefront.</li>
            </ol>
          </div>
        </div>
      ) : (
        <div>
          <div className="bg-white border border-[#E5DEC9] p-5 mb-6 flex flex-wrap items-center gap-4" data-testid="social-connected-card">
            <div className="flex items-center gap-2">
              <Instagram size={18} className="text-[#C5A059]" />
              <span className="text-sm font-medium">{status.instagram_username ? `@${status.instagram_username}` : "Not linked"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Facebook size={18} className="text-[#C5A059]" />
              <span className="text-sm font-medium">{status.page_name || "Not linked"}</span>
            </div>
            <span className="text-xs text-[#9a938a]">{status.items} post(s) synced{status.last_sync ? ` · last sync ${new Date(status.last_sync).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}</span>
            <div className="ml-auto flex gap-2">
              <button onClick={sync} disabled={syncing} data-testid="social-sync-btn" className="tq-btn-dark !py-2.5"><RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync Now"}</button>
              <button onClick={disconnect} data-testid="social-disconnect-btn" className="tq-btn-outline !py-2.5 text-[#8B1E21]"><Unplug size={14} /> Disconnect</button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="bg-white border border-[#E5DEC9] p-10 text-center text-sm text-[#6E6D6A]" data-testid="social-items-empty">No posts synced yet. Click "Sync Now" to import your Instagram & Facebook posts.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map((it) => (
                <div key={`${it.source}-${it.external_id}`} className="bg-white border border-[#E5DEC9] group relative" data-testid={`social-item-${it.external_id}`}>
                  <div className="aspect-square bg-[#F4EFE6] overflow-hidden">
                    {(it.thumbnail_url || it.media_url) ? (
                      <img src={it.thumbnail_url || it.media_url} alt="" className="w-full h-full object-cover" />
                    ) : <div className="w-full h-full flex items-center justify-center text-[#9a938a]"><Instagram size={24} /></div>}
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs text-[#6E6D6A] line-clamp-2 min-h-[2rem]">{it.caption || "(no caption)"}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${it.source === "instagram" ? "bg-[#D4AF37]/15 text-[#8C7A5E]" : "bg-blue-50 text-blue-700"}`}>{it.source}</span>
                      <div className="flex items-center gap-1.5">
                        {(it.product_ids || []).length > 0 && <span className="text-[10px] text-[#C5A059] font-semibold flex items-center gap-0.5"><Link2 size={10} />{it.product_ids.length}</span>}
                        <button onClick={() => setEditing({ ...it, product_ids: [...(it.product_ids || [])], products: undefined })} data-testid={`social-edit-${it.external_id}`} aria-label="Edit post" className="text-xs text-[#C5A059] underline">Edit</button>
                      </div>
                    </div>
                  </div>
                  {!it.visible && <span className="absolute top-2 left-2 bg-[#121212]/80 text-white text-[9px] uppercase tracking-wider px-2 py-1 flex items-center gap-1"><EyeOff size={10} /> Hidden</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="social-edit-modal">
          <div className="bg-white w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <p className="font-serif text-xl">Edit Post</p>
              <button onClick={() => setEditing(null)} aria-label="Close"><X size={20} /></button>
            </div>
            {(editing.thumbnail_url || editing.media_url) && <img src={editing.thumbnail_url || editing.media_url} alt="" className="w-full aspect-square object-cover bg-[#F4EFE6] mb-4" />}
            <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
              <input type="checkbox" checked={editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} data-testid="social-edit-visible" className="accent-[#D4AF37] w-4 h-4" />
              <span className="flex items-center gap-1.5">{editing.visible ? <Eye size={14} /> : <EyeOff size={14} />} Visible on storefront</span>
            </label>
            <label className="tq-overline !text-[10px] block mb-1">Display Position (lower = first)</label>
            <input type="number" value={editing.position} onChange={(e) => setEditing({ ...editing, position: Number(e.target.value) })} data-testid="social-edit-position" className="tq-input mb-4" />
            <label className="tq-overline !text-[10px] block mb-1">Tagged Products (Shop the Look)</label>
            {editing.product_ids.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {editing.product_ids.map((pid) => (
                  <span key={pid} className="text-xs bg-[#F4EFE6] px-2 py-1 flex items-center gap-1.5">
                    {pid.slice(0, 8)}…
                    <button type="button" onClick={() => setEditing({ ...editing, product_ids: editing.product_ids.filter((x) => x !== pid) })} aria-label="Remove product"><Trash2 size={11} className="text-[#8B1E21]" /></button>
                  </span>
                ))}
              </div>
            )}
            <ProductPicker onPick={(p) => !editing.product_ids.includes(p.id) && setEditing({ ...editing, product_ids: [...editing.product_ids, p.id] })} />
            <div className="flex gap-2 mt-5">
              <button onClick={saveItem} data-testid="social-edit-save" className="tq-btn-dark flex-1">Save</button>
              <button onClick={() => setEditing(null)} className="tq-btn-outline">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
